import { useEffect } from 'react';
import { useSelector } from 'react-redux';
import store, { brokerActions } from '@apply/adminStore';
import { useBrokerDetailsStore } from '../../state/brokerDetailsStore';
import { useBrokerDetailsQuery } from '../../queries/brokerDetailsQueries';
import { brokerTitles } from '../../../../common/SharedConstants';
import { ListApi, TemplatesApi } from '@apply/api';
import { applicationInsights } from '../../../../ApplicationInsights';

interface UseBrokerAccountSetupInitReturn {
    isLoading: boolean;
}

export const useBrokerAccountSetupInit = (): UseBrokerAccountSetupInitReturn => {
    /* eslint-disable @typescript-eslint/no-explicit-any */
    // Redux state is typed as any
    const bfiUser = useSelector((state: any) => state.bfiUser);
    const brokerId = useSelector((state: any) => state.brokerState?.brokerId);
    /* eslint-enable @typescript-eslint/no-explicit-any */

    const { data: broker, isLoading } = useBrokerDetailsQuery(brokerId, bfiUser.admin);

    // Initialization: user context, eSign HTML, lists
    useEffect(() => {
        const s = useBrokerDetailsStore.getState();
        s.updateFields({ isAdmin: bfiUser.admin, isBroker: bfiUser.isBroker, brokerId });

        const templatesApi = new TemplatesApi.TemplatesApi(applicationInsights);
        const listApi = new ListApi.ListApi(applicationInsights);

        const init = async () => {
            const promises: Promise<void | PromiseLike<void>>[] = [];

            // Load eSign HTML
            promises.push(
                templatesApi.getUserAgreementHtml(0).then((response) => {
                    if (response.statusCode === 200) {
                        useBrokerDetailsStore.getState().updateFields({ userAgreementHtml: response.result });
                    }
                })
            );

            if (bfiUser.admin) {
                // Load ICCA templates
                promises.push(
                    listApi.getIccas().then((response) => {
                        if (response.statusCode !== 200) return;
                        const state = useBrokerDetailsStore.getState();
                        const defaultIcca = response.result[0];
                        state.updateFields({
                            agreements: response.result,
                            ...(!state.iccaTemplateId && defaultIcca ? { iccaTemplateId: defaultIcca.key } : {}),
                        });
                    })
                );

                // Load BFI employees
                promises.push(
                    listApi.getBfiEmployees().then((data) => {
                        useBrokerDetailsStore.getState().updateFields({ pointsOfContact: data });
                    })
                );
            }

            if (bfiUser.isBroker) {
                store.dispatch(brokerActions.setBrokerId({ brokerId: bfiUser.broker.id }));
            }

            await Promise.all(promises);
        };

        init();

        return () => {
            useBrokerDetailsStore.getState().reset();
        };
    }, []);

    // Initialize form from broker data
    useEffect(() => {
        useBrokerDetailsStore.getState().initializeFromBroker(broker, {
            isAdmin: bfiUser.admin,
            isBroker: bfiUser.isBroker,
            brokerId,
            brokerTitles,
        });
    }, [broker]);

    return { isLoading };
};
